import { ProductDetailsType } from 'types/ProductDetailsType';

export const translationNodeSelectorClassName = 'translation-node-to-translate';

class EphemeralStorage {
  private _storage: any = {};

  constructor() {}

  public get(key: string) {
    return this._storage[key];
  }

  public set(key: string, value: any) {
    this._storage[key] = value;
  }
}

class TranslationHandler {
  private _ephemeralStoragesMap: any = {};
  private _cachedTranslationsEphemeralStorageBucket = 'cached-translations';
  private _translationQueue: string[] = [];
  private _queueTimer: NodeJS.Timeout | null = null;
  private _translationPromises: Map<
    string,
    { resolve: (value: string) => void }
  > = new Map();
  private _wordsSize = 500;

  constructor() {
    const w: any = window;
    const cachedTranslationsMap = w.cachedTranslationsMap;
    if (cachedTranslationsMap) {
      Object.keys(cachedTranslationsMap).forEach((key) => {
        this._setTranslationInCache(key, cachedTranslationsMap[key]);
      });
    }
  }

  private _extractLeafTextNodes() {
    const nodes = document.querySelectorAll(
      `.${translationNodeSelectorClassName}`
    );
    const leafTextNodes: Node[] = [];

    const processNode = (node: Node) => {
      // Skip if the node or any of its parents has the skip-translation class
      let currentNode = node as Element;
      while (currentNode && currentNode.nodeType === Node.ELEMENT_NODE) {
        if (currentNode.classList?.contains('skip-translation')) {
          return;
        }
        currentNode = currentNode.parentElement as Element;
      }

      // If this is a text node with non-whitespace content, add it to our collection
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
        leafTextNodes.push(node);
        return;
      }

      // If it's an element node, process its children
      if (node.nodeType === Node.ELEMENT_NODE) {
        // If the node has no children, we can skip it
        if (!node.hasChildNodes()) {
          return;
        }

        // Process all child nodes
        node.childNodes.forEach((childNode) => {
          processNode(childNode);
        });
      }
    };

    // Process each node with the specified class
    nodes.forEach((node) => {
      processNode(node);
    });

    return leafTextNodes;
  }

  startNodesWatcher(callback: (textNodes: Node[]) => void) {
    // Initial extraction
    const initialNodes = this._extractLeafTextNodes();
    callback(initialNodes);

    // Set up mutation observer to detect DOM changes
    const observer = new MutationObserver(() => {
      const nodes = this._extractLeafTextNodes();
      callback(nodes);
    });

    // Start observing the document with configured parameters
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Return the observer so it can be disconnected if needed
    return observer;
  }

  public getValueFromEphemeralStorage(bucket: string, key: string) {
    if (!this._ephemeralStoragesMap[bucket]) {
      this._ephemeralStoragesMap[bucket] = new EphemeralStorage();
    }
    return this._ephemeralStoragesMap[bucket].get(key);
  }

  public setValueInEphemeralStorage(
    bucket: string,
    key: string,
    value: string
  ) {
    if (!this._ephemeralStoragesMap[bucket]) {
      this._ephemeralStoragesMap[bucket] = new EphemeralStorage();
    }
    this._ephemeralStoragesMap[bucket].set(key, value);
  }

  private _requestTranslation(text: string): Promise<string> {
    return new Promise((resolve) => {
      // Skip if the text is already in the queue
      if (this._translationQueue.includes(text)) {
        return;
      }
      // Add the text to the queue and store its resolver
      this._translationQueue.push(text);
      this._translationPromises.set(text, { resolve });

      // If this is the first item, start the timer
      if (this._translationQueue.length === 1 && !this._queueTimer) {
        this._queueTimer = setTimeout(() => this._processQueue(), 2000);
      }

      // If we've reached n items, process the queue immediately
      if (
        this._translationQueue.join(' ').split(/\s+/).length >= this._wordsSize
      ) {
        if (this._queueTimer) {
          clearTimeout(this._queueTimer);
          this._queueTimer = null;
        }
        this._processQueue();
      }
    });
  }

  private async _processQueue(): Promise<void> {
    const itemsToProcess = [...this._translationQueue];
    this._translationQueue = [];
    this._queueTimer = null;

    try {
      // Call the translation API with the batch of texts
      const experienceSlug = window.location.pathname
        .split('/')[2]
        .split('?')
        .shift();
      const response = await fetch(
        `/app_api/products/${experienceSlug}/translate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ textsList: itemsToProcess }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Translation request failed with status: ${response.status}`
        );
      }

      const data = await response.json();
      const translatedTextsMap = data.translatedTextsMap;

      // Resolve each promise with the corresponding translated text
      itemsToProcess.forEach((item) => {
        const translatedText = translatedTextsMap[item] || item;
        const promise = this._translationPromises.get(item);
        if (promise) {
          promise.resolve(translatedText);
          this._translationPromises.delete(item);
        }
      });
    } catch (error) {
      console.error('Translation error:', error);

      // In case of error, resolve with original text
      itemsToProcess.forEach((item) => {
        const promise = this._translationPromises.get(item);
        if (promise) {
          promise.resolve(item);
          this._translationPromises.delete(item);
        }
      });
    }
  }

  private _isTextTranslatable(text: string) {
    // Empty text
    if (!text.trim()) {
      return false;
    }
    // Is number
    if (!isNaN(Number(text))) {
      return false;
    }
    // is email
    if (text.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      return false;
    }
    // Has at least one letter
    if (!text.match(/[a-zA-Z]/)) {
      return false;
    }
    return true;
  }

  private _getTranslationFromCache(text: string) {
    return this.getValueFromEphemeralStorage(
      this._cachedTranslationsEphemeralStorageBucket,
      text
    );
  }

  private _setTranslationInCache(text: string, translation: string) {
    this.setValueInEphemeralStorage(
      this._cachedTranslationsEphemeralStorageBucket,
      text,
      translation
    );
  }

  public async translateText(text: string) {
    // Check if text is translatable
    if (!this._isTextTranslatable(text)) {
      return text;
    }
    // If cached resolve
    const cachedTranslation = this._getTranslationFromCache(text);
    if (!!cachedTranslation) {
      return cachedTranslation;
    }
    // If not cached request translation
    const translation = await this._requestTranslation(text);
    // Return translation
    this._setTranslationInCache(text, translation);
    return translation;
  }
}

class NodeTranslationProcessor {
  private _node: Node;
  private _translationHandler: TranslationHandler;
  private _nodePath: string;
  private _cachedTranslationPathEphemeralStorageBucket = 'node-translations';

  constructor(node: Node, translationHandler: TranslationHandler) {
    this._node = node;
    this._translationHandler = translationHandler;
    this._nodePath = this._extractNodePath();
  }

  private _extractNodePath() {
    let nodePath = '';
    let currentNode = this._node as Element;

    while (currentNode) {
      const tagName = currentNode.tagName?.toLowerCase();
      if (tagName) {
        // Find the position of the current element among siblings of the same type
        let position = 0;
        let sibling = currentNode.previousElementSibling;
        while (sibling) {
          if (sibling.tagName.toLowerCase() === tagName) {
            position++;
          }
          sibling = sibling.previousElementSibling;
        }

        // Add position index only if it's not the first element (0)
        const positionSuffix = position > 0 ? `[${position}]` : '';

        // Include ID if available
        const idAttr = currentNode.id ? `(id="${currentNode.id}")` : '';

        // Include class if available
        const classAttr = currentNode.className
          ? `(class="${currentNode.className}")`
          : '';

        nodePath = `>${tagName}${positionSuffix}${idAttr}${classAttr}${nodePath}`;
      }

      const parentElement = currentNode.parentElement;
      if (!parentElement) break;
      currentNode = parentElement;
    }

    // Remove the leading '>' if it exists
    return nodePath.startsWith('>') ? nodePath.substring(1) : nodePath;
  }

  private _applyTranslation(translation: string) {
    const buffNode: any = this._node;
    if (buffNode._translated) {
      return;
    }
    buffNode._translated = true;
    this._node.textContent = translation;
  }

  private _translateText(textToTranslate: string) {
    return this._translationHandler.translateText(textToTranslate);
  }

  async processNode() {
    const text = this._node.textContent;
    if (!text) return;
    // Check if the translation is cached
    const cachedTranslation =
      this._translationHandler.getValueFromEphemeralStorage(
        this._cachedTranslationPathEphemeralStorageBucket,
        this._nodePath
      );
    // if (cachedTranslation) {
    //   this._applyTranslation(cachedTranslation);
    //   return;
    // }
    // Translate the text and apply it
    const translatedText = await this._translateText(text);
    this._applyTranslation(translatedText);
    // Cache the translation
    this._translationHandler.setValueInEphemeralStorage(
      this._cachedTranslationPathEphemeralStorageBucket,
      this._nodePath,
      translatedText
    );
  }
}

export default async function handleTranslation({}: {
  productDetails: ProductDetailsType;
}) {
  const translationHandler = new TranslationHandler();
  if (
    !localStorage.getItem('default-language') &&
    navigator.language !== 'en'
  ) {
    localStorage.setItem('default-language', navigator.language);
    await fetch('/app_api/user/language', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ language: navigator.language }),
    });
    localStorage.setItem('translation-refresh-executed', 'true');
    window.location.reload();
  } else localStorage.setItem('translation-refresh-executed', 'true');
  translationHandler.startNodesWatcher((textNodes) => {
    for (const textNode of textNodes) {
      const nodeTranslationProcessor = new NodeTranslationProcessor(
        textNode,
        translationHandler
      );
      nodeTranslationProcessor.processNode();
    }
  });
}
